import ViolationStatus from "../../enums/ViolationStatus";
import { Tooltip, Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import ComplianceStatus from "../../enums/ComplianceStatus";
import { useTranslation } from "react-i18next";
import { Chip } from "@outpave/ds";
import { isTerminalStatus } from "utils/transactions/transactionGroups";
import { transactionStatusEntry } from "./RenderTransactionStatus";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CheckListIcon,
  Invoice01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import type { ChipTone } from "@outpave/ds";

export enum ComplianceStatusDisplay {
  Complete = "Complete",
  Missing = "Missing",
  Mismatch = "Mismatch",
  Lost = "Lost",
  Exempt = "Exempt",
  Declined = "Declined",
  Canceled = "Canceled",
  Returned = "Returned",
  Refunded = "Refunded",
  Failed = "Failed",
  Reversed = "Reversed",
}

interface ViolationFacet {
  violationStatus?: string;
  isExempt?: boolean;
}

interface RenderComplianceProps {
  policyStatus?: unknown;
  /** The transaction status; a terminal one outranks the policy check. */
  policyStatusDisplay?: unknown;
  /** Lets a declined reimbursement read "Rejected", as the status column does. */
  transactionType?: string;
  /** Render nothing for a terminal status, for a table whose status column already says it. */
  hideTerminal?: boolean;
  sx?: SxProps<Theme>;
}

export interface ComplianceBadge {
  label: string;
  tone: ChipTone;
  violations: string[];
}

/** A missing or malformed `policyStatus` reads as unmet instead of throwing. */
const readFacet = (
  source: unknown,
  key: "receipt" | "allocation",
): ViolationFacet => {
  if (typeof source !== "object" || source === null) return {};
  const value = (source as Record<string, unknown>)[key];
  if (typeof value !== "object" || value === null) return {};
  return value as ViolationFacet;
};

/** A terminal status outranks the receipt and allocation check. */
export const resolveComplianceBadge = (
  policyStatus: unknown,
  policyStatusDisplay: unknown,
  transactionType?: string,
): ComplianceBadge => {
  // The status column's base word; its dispute and workflow overrides are not mirrored.
  if (isTerminalStatus(policyStatusDisplay)) {
    const { name } = transactionStatusEntry(
      policyStatusDisplay,
      undefined,
      undefined,
      transactionType,
    );
    return { label: name, tone: "neutral", violations: [] };
  }

  const receipt = readFacet(policyStatus, "receipt");
  const allocation = readFacet(policyStatus, "allocation");

  const isReceiptExempt =
    receipt.violationStatus === ViolationStatus.EXEMPT ||
    receipt.isExempt === true;
  const isAllocationExempt =
    allocation.violationStatus === ViolationStatus.EXEMPT ||
    allocation.isExempt === true;
  if (isReceiptExempt && isAllocationExempt) {
    return {
      label: ComplianceStatusDisplay.Exempt,
      tone: "info",
      violations: [],
    };
  }

  const violations: string[] = [];

  if (receipt.violationStatus === ViolationStatus.LOST) {
    violations.push(ComplianceStatus.LostReceipt);
  } else if (
    receipt.violationStatus === ViolationStatus.MISMATCH &&
    !isReceiptExempt
  ) {
    violations.push(ComplianceStatus.MismatchReceipt);
  } else if (
    !isReceiptExempt &&
    receipt.violationStatus !== ViolationStatus.MATCH &&
    receipt.violationStatus !== ViolationStatus.APPROVED_LOST
  ) {
    violations.push(ComplianceStatus.MissingReceipt);
  }

  if (
    !isAllocationExempt &&
    allocation.violationStatus !== ViolationStatus.MATCH
  ) {
    violations.push(ComplianceStatus.MissingAllocation);
  }

  if (violations.length === 0) {
    return {
      label: ComplianceStatusDisplay.Complete,
      tone: "positive",
      violations: [],
    };
  }

  const onlyIssue = violations.length === 1 ? violations[0] : null;
  let label = ComplianceStatusDisplay.Missing;
  if (onlyIssue === ComplianceStatus.MismatchReceipt) {
    label = ComplianceStatusDisplay.Mismatch;
  } else if (onlyIssue === ComplianceStatus.LostReceipt) {
    label = ComplianceStatusDisplay.Lost;
  }

  return { label, tone: "warning", violations };
};

export type ComplianceIssue =
  | "receipt-missing"
  | "receipt-lost"
  | "receipt-mismatch"
  | "allocation";

/** The facets needing attention. Empty means nothing to show. */
export const resolveComplianceIssues = (
  policyStatus: unknown,
): ComplianceIssue[] => {
  const receipt = readFacet(policyStatus, "receipt");
  const allocation = readFacet(policyStatus, "allocation");

  const receiptExempt =
    receipt.violationStatus === ViolationStatus.EXEMPT ||
    receipt.isExempt === true;
  const allocationExempt =
    allocation.violationStatus === ViolationStatus.EXEMPT ||
    allocation.isExempt === true;

  if (receiptExempt && allocationExempt) return [];

  const issues: ComplianceIssue[] = [];

  if (!receiptExempt) {
    if (receipt.violationStatus === ViolationStatus.MISMATCH) {
      issues.push("receipt-mismatch");
    } else if (receipt.violationStatus === ViolationStatus.LOST) {
      issues.push("receipt-lost");
    } else if (
      receipt.violationStatus !== ViolationStatus.MATCH &&
      receipt.violationStatus !== ViolationStatus.APPROVED_LOST
    ) {
      issues.push("receipt-missing");
    }
  }

  if (
    !allocationExempt &&
    allocation.violationStatus !== ViolationStatus.MATCH
  ) {
    issues.push("allocation");
  }

  return issues;
};

/** Same glyphs ComplianceStats already uses for these two facets. */
const ISSUE_UI: Record<
  ComplianceIssue,
  { icon: typeof Invoice01Icon; label: string; tone: "warning" }
> = {
  "receipt-missing": {
    icon: Invoice01Icon,
    label: ComplianceStatus.MissingReceipt,
    tone: "warning",
  },
  "receipt-lost": {
    icon: Invoice01Icon,
    label: ComplianceStatus.LostReceipt,
    tone: "warning",
  },
  "receipt-mismatch": {
    icon: Invoice01Icon,
    label: ComplianceStatus.MismatchReceipt,
    tone: "warning",
  },
  allocation: {
    icon: CheckListIcon,
    label: ComplianceStatus.MissingAllocation,
    tone: "warning",
  },
};

const TONE_TOKENS: Record<"warning", [string, string]> = {
  warning: [
    "var(--color-status-warning-bg)",
    "var(--color-status-warning-text)",
  ],
};

const ComplianceCircle: React.FC<{ issue: ComplianceIssue }> = ({ issue }) => {
  const { t } = useTranslation();
  const { icon, label, tone } = ISSUE_UI[issue];
  const [background, color] = TONE_TOKENS[tone];

  return (
    <Tooltip disableInteractive placement="top" title={t(label)}>
      <Box
        component="span"
        tabIndex={0}
        role="img"
        aria-label={t(label)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          width: 24,
          height: 24,
          borderRadius: "var(--radius-full)",
          backgroundColor: background,
          color,
        }}
      >
        <HugeiconsIcon icon={icon} size={14} />
      </Box>
    </Tooltip>
  );
};

/** One circle per facet needing attention; nothing when clean. */
export const RenderCompliance: React.FC<RenderComplianceProps> = ({
  policyStatus,
  policyStatusDisplay,
  transactionType,
  hideTerminal = false,
  sx,
}) => {
  const { t } = useTranslation();
  const badge = resolveComplianceBadge(
    policyStatus,
    policyStatusDisplay,
    transactionType,
  );

  if (isTerminalStatus(policyStatusDisplay)) {
    if (hideTerminal) return null;
    return (
      <Chip tone={badge.tone} size="sm" sx={sx}>
        {t(badge.label)}
      </Chip>
    );
  }

  const issues = resolveComplianceIssues(policyStatus);
  if (issues.length === 0) return null;

  return (
    <Box sx={{ display: "inline-flex", gap: "var(--space-2)", ...sx }}>
      {issues.map((issue) => (
        <ComplianceCircle key={issue} issue={issue} />
      ))}
    </Box>
  );
};
